import React, { useEffect, useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ICareHome } from '../../interfaces/CareHome/ICareHome.ts';
import { FORCE_URINE_DETECTION_ACTIVE } from '../../constants/featureActivationFlags.ts';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import FeatureActivationCard from './FeatureActivationCard.tsx';

interface FeatureActivationFormValues {
  incontinenceManagementUrine: boolean | null;
  safeZone: boolean | null;
  bedsoresPrevention: boolean | null;
  fallDetection: boolean | null;
}

interface FeatureActivationTabProps {
  residentName: string;
  careHome: ICareHome | null;
  isNewResident?: boolean;
}

const FEATURE_FIELDS: (keyof FeatureActivationFormValues)[] = [
  'incontinenceManagementUrine',
  'bedsoresPrevention',
  'fallDetection',
  'safeZone',
];

const FeatureActivationTab: React.FC<FeatureActivationTabProps> = ({
  residentName,
  isNewResident = false,
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FeatureActivationFormValues>();
  const formValues = watch();

  useEffect(() => {
    if (FORCE_URINE_DETECTION_ACTIVE) {
      setValue('incontinenceManagementUrine', true, {
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [setValue]);

  const decidedCount = useMemo(
    () =>
      FEATURE_FIELDS.filter((field) => formValues[field] !== null).length,
    [formValues]
  );

  const remainingCount = FEATURE_FIELDS.length - decidedCount;
  const allDecided = remainingCount === 0;
  const allFeaturesDeclined =
    allDecided &&
    FEATURE_FIELDS.every((field) => formValues[field] === false);
  const progressPercent = (decidedCount / FEATURE_FIELDS.length) * 100;
  const displayName = residentName.trim() || '{{name}}';

  const features = useMemo(
    () => [
      {
        field: 'incontinenceManagementUrine' as const,
        feature: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
        title: t('Incontinence Management (Urine)'),
        description: t('incontinenceManagementUrineDescription'),
        footerNote: t('onlyAvailableWithSeniSmart'),
        question: t('activateUrineDetection'),
      },
      {
        field: 'bedsoresPrevention' as const,
        feature: ResidentFeature.REPOSITIONING_MANAGEMENT,
        title: t('bedsoresPrevention'),
        description: t('bedsoresPreventionDescription'),
        question: t('activateBodyPositionDetection'),
      },
      {
        field: 'fallDetection' as const,
        feature: ResidentFeature.FALL_MANAGEMENT,
        title: t('fallDetection'),
        description: t('fallDetectionDescription'),
        question: t('activateFallDetection'),
      },
      {
        field: 'safeZone' as const,
        feature: ResidentFeature.SECURE_ZONE,
        title: t('safeZone'),
        description: t('safeZoneDescription').replace(/\{\{name\}\}/g, displayName),
        question: t('activateSafeZone'),
      },
    ],
    [t, displayName]
  );

  return (
    <Flex direction="column" alignItems="center" pb="20px">
      <Box maxWidth="949px" w="100%" mb="32px">
        <Flex
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '24px' }}
        >
          <Text fontSize="25px" fontWeight={500} color="#000">
            {t('featureActivation')}
          </Text>

          <Flex
            align="center"
            gap="12px"
            bg="#F3F2F2"
            borderRadius="full"
            h="38px"
            px="20px"
            py="10px"
          >
            <Text fontSize="14px" color="#000" whiteSpace="nowrap">
              {t('XofYDecided', {
                decided: decidedCount,
                total: FEATURE_FIELDS.length,
              })}
            </Text>
            <Box
              w="60px"
              h="3px"
              bg="#C6C6C6"
              borderRadius="full"
              overflow="hidden"
              flexShrink={0}
            >
              <Box
                h="100%"
                bg="alea.500"
                borderRadius="full"
                w={`${progressPercent}%`}
                transition="width 0.2s ease"
              />
            </Box>
          </Flex>
        </Flex>
      </Box>

      <Box maxWidth="949px" w="100%">
        <Flex direction="column" gap="16px">
          {features.map((item) => {
            const isForcedUrineDetection =
              FORCE_URINE_DETECTION_ACTIVE &&
              item.field === 'incontinenceManagementUrine';

            return (
              <FeatureActivationCard
                key={item.field}
                feature={item.feature}
                title={item.title}
                description={item.description}
                footerNote={item.footerNote}
                question={item.question}
                value={
                  isForcedUrineDetection ? true : formValues[item.field]
                }
                disabled={isForcedUrineDetection}
                onYes={() =>
                  setValue(item.field, true, {
                    shouldValidate: true,
                    shouldTouch: true,
                  })
                }
                onNo={() =>
                  setValue(item.field, false, {
                    shouldValidate: true,
                    shouldTouch: true,
                  })
                }
              />
            );
          })}
        </Flex>

        {remainingCount > 0 && (
          <Text
            mt="16px"
            fontSize="15px"
            fontWeight={300}
            color="#000"
            textAlign="right"
          >
            {remainingCount === 1
              ? t('XfeaturesTbd', { count: remainingCount })
              : t('XfeaturesTbdPlural', { count: remainingCount })}
          </Text>
        )}

        {isNewResident && allFeaturesDeclined && (
          <Text
            mt="16px"
            fontSize="16px"
            fontWeight={400}
            color="#000"
            textAlign="right"
          >
            {t('activateAtLeastOneFeature')}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default FeatureActivationTab;
